import React, { useMemo } from "react";

import type { Control, UseControllerProps, UseFormReturn, FieldValues, UseFormHandleSubmit } from "react-hook-form";

type SmartFormChildPropsT = {
  controllerProps: Omit<UseControllerProps, "name">;
};

type SmartFormPropsT<TFieldValues extends FieldValues = FieldValues> = {
  children: React.ReactNode;
  onSubmit?: ReturnType<UseFormHandleSubmit<FieldValues>>;
  methods: Omit<UseFormReturn<TFieldValues>, "handleSubmit">;
  formProps?: Record<string, unknown>;
};

type SmartFormContextT = SmartFormChildPropsT;

const SmartFormContext = React.createContext<SmartFormContextT | undefined>(undefined);
SmartFormContext.displayName = "SmartFormContext";

export default function SmartForm<TFieldValues extends FieldValues = FieldValues>({
  children,
  onSubmit,
  methods,
  formProps,
}: SmartFormPropsT<TFieldValues>) {
  const { control } = methods;

  // Fields find their value by name, so the context holds the control without this form's field types.
  const value: SmartFormContextT = useMemo(() => ({ controllerProps: { control: control as Control } }), [control]);

  return (
    <SmartFormContext.Provider value={value}>
      {onSubmit ? (
        <form {...formProps} onSubmit={onSubmit}>
          {children}
        </form>
      ) : (
        <form {...formProps}>{children}</form>
      )}
    </SmartFormContext.Provider>
  );
}

function useSmartFormContext() {
  const context = React.useContext(SmartFormContext);
  if (context === undefined) {
    throw new Error("useSmartForm must be used within a SmartForm");
  }
  return context;
}

export type { SmartFormPropsT, SmartFormChildPropsT };
export { useSmartFormContext };
